
import React from "react";
import { StatCard, DonutWithLegend } from "../../../components/ui";
import { fmt } from "../../../lib/formatters";
import { Maximize2, Minimize2, ShoppingCart, ShoppingBag, Users, Package, IndianRupee, TrendingUp, History, FileText, Clock, Truck, CheckCircle, RotateCcw } from "lucide-react";
import { useInventoryStats } from "../../../hooks/useGlobalStats";
import { STATUS_VARIANT_MAP, RETURNS_SUB_TABS, INVENTORY_SUB_TABS } from "../../../constants/globalConstants";

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#6366F1', '#EC4899', '#8B5CF6'];

export const InventoryStats = ({
  activeTab,
  viewMode = "inventory",
  subTab = "items",
  isExpanded,
  setIsExpanded,
  inventory = [],
  sales = [],
  returns = [],
  requests = [],
  purchaseReturns = [],
  purchases = [],
  openingStocks = [],
  damagedItems = []
}) => {
  const stats = useInventoryStats({
    inventory,
    sales,
    returns,
    requests,
    purchaseReturns,
    purchases,
    openingStocks,
    activeTab
  });

  const { stock, sales: salesStats, requests: requestStats, returns: returnStats } = stats;


  const renderStatsContent = () => {
    // 1. Returns View Mode
    if (viewMode === "returns") {
      const typeLabel = subTab === RETURNS_SUB_TABS.MASTER ? "Master" : 
                        subTab === RETURNS_SUB_TABS.PURCHASES ? "Purchases" : 
                        subTab === RETURNS_SUB_TABS.SITE ? "Site" : 
                        subTab === RETURNS_SUB_TABS.SALES ? "Sales" : "Damaged";
      
      const isDamaged = subTab === RETURNS_SUB_TABS.DAMAGED;
      
      const currentReturns = (subTab === RETURNS_SUB_TABS.MASTER ? returns : 
                           subTab === RETURNS_SUB_TABS.PURCHASES ? returns.filter(r => r.rawType === 'purchase' || r.returnType === 'Purchase') :
                           subTab === RETURNS_SUB_TABS.SITE ? returns.filter(r => r.rawType === 'site' || r.returnType === 'Site') :
                           subTab === RETURNS_SUB_TABS.SALES ? returns.filter(r => r.rawType === 'sale' || r.returnType === 'SALE') :
                           damagedItems || []);

      let totalItemsCount = 0;
      let totalAmountVal = 0;

      if (isDamaged) {
        totalItemsCount = currentReturns.reduce((sum, r) => sum + (Number(r.totalDamagedQty) || 0), 0);
        totalAmountVal = currentReturns.reduce((sum, r) => {
          let value = Number(r.totalDamagedValue) || 0;
          if (value === 0 && r.damagedItems) {
            value = r.damagedItems.reduce((acc, curr) => acc + ((curr.damagedQty || 0) * (curr.rate || 0)), 0);
          }
          return sum + value;
        }, 0);
      } else {
        totalItemsCount = currentReturns.reduce((sum, r) => sum + (r.items?.reduce((is, it) => is + (Number(it.quantity || it.qty || 0)), 0) || 0), 0);
        totalAmountVal = currentReturns.reduce((sum, r) => sum + (Number(r.totalAmount || r.refundAmount || r.amount || 0)), 0);
      }

      return (
        <>
          <StatCard
            variant="luxury"
            title="Total Returns"
            iconColor="text-brand-orange"
            value={currentReturns.length}
            isExpanded={false}
            topColor="border-t-[#5d6b8b]"
            iconHoverColor="group-hover:text-[#ffa000]"
            illustration={<RotateCcw className="w-10 h-10" strokeWidth={1.5} />}
          />
          <StatCard
            variant="luxury"
            title="Return Type"
            value={typeLabel}
            isExpanded={false}
            topColor="border-t-[#5d6b8b]"
            iconHoverColor="group-hover:text-[#ffa000]"
            illustration={<History className="w-10 h-10" strokeWidth={1.5} />}
          />
          <StatCard
            variant="luxury"
            title="Return Items"
            value={totalItemsCount}
            isExpanded={false}
            topColor="border-t-[#5d6b8b]"
            iconHoverColor="group-hover:text-[#ffa000]"
            illustration={<Package className="w-10 h-10" strokeWidth={1.5} />}
          />
          <StatCard
            variant="luxury"
            title="Total Amount"
            value={`${fmt(totalAmountVal)}`}
            isExpanded={false}
            topColor="border-t-[#5d6b8b]"
            iconHoverColor="group-hover:text-[#ffa000]"
            illustration={<IndianRupee className="w-10 h-10" strokeWidth={1.5} />}
          />
        </>
      );
    }

    // 2. Requests View Mode
    if (viewMode === "requests") {
      return (
        <>
          <StatCard
            variant="luxury"
            title="Total Requests"
            iconColor="text-brand-orange"
            value={requestStats.total}
            sub="All material requests"
            isExpanded={isExpanded}
            topColor="border-t-[#5d6b8b]"
            iconHoverColor="group-hover:text-[#ffa000]"
            illustration={<FileText className="w-10 h-10" strokeWidth={1.5} />}
          />
          <StatCard
            variant="luxury"
            title="Pending Approval"
            value={requestStats.pending}
            sub="Awaiting review"
            isExpanded={isExpanded}
            topColor="border-t-[#5d6b8b]"
            iconHoverColor="group-hover:text-[#ffa000]"
            illustration={<Clock className="w-10 h-10" strokeWidth={1.5} />}
          />
          <StatCard
            variant="luxury"
            title="In Transit"
            value={requestStats.moving}
            sub="Currently moving"
            isExpanded={isExpanded}
            topColor="border-t-[#5d6b8b]"
            iconHoverColor="group-hover:text-[#ffa000]"
            illustration={<Truck className="w-10 h-10" strokeWidth={1.5} />}
          />
          <StatCard
            variant="luxury"
            title="Completed"
            value={requestStats.completed}
            sub="Successfully delivered"
            isExpanded={isExpanded}
            topColor="border-t-[#5d6b8b]"
            iconHoverColor="group-hover:text-[#ffa000]"
            illustration={<CheckCircle className="w-10 h-10" strokeWidth={1.5} />}
          />
        </>
      );
    }

    // 3. Inventory Sub-Tabs
    switch (subTab) {
      case INVENTORY_SUB_TABS.PURCHASES:
        const { purchases: pStats } = stats;
        return (
          <>
            <StatCard
              variant="luxury"
              title="Total Purchases"
              iconColor="text-brand-orange"
              value={`${fmt(pStats.totalSpend)}`}
              isExpanded={isExpanded}
              topColor="border-t-[#5d6b8b]"
              iconHoverColor="group-hover:text-[#ffa000]"
              illustration={<IndianRupee className="w-10 h-10" strokeWidth={1.5} />}
            >
              <DonutWithLegend data={stock.distribution} label="Supplier Distribution" labelColor="text-blue-600" />
            </StatCard>
            <StatCard
              variant="luxury"
              title="Items Purchased"
              value={pStats.totalQty}
              isExpanded={isExpanded}
              topColor="border-t-[#5d6b8b]"
              iconHoverColor="group-hover:text-[#ffa000]"
              illustration={<Package className="w-10 h-10" strokeWidth={1.5} />}
            />
            <StatCard
              variant="luxury"
              title="Transactions"
              value={pStats.transactions}
              isExpanded={isExpanded}
              topColor="border-t-[#5d6b8b]"
              iconHoverColor="group-hover:text-[#ffa000]"
              illustration={<History className="w-10 h-10" strokeWidth={1.5} />}
            />
            <StatCard
              variant="luxury"
              title="Total Vendors"
              value={pStats.suppliersCount}
              isExpanded={isExpanded}
              topColor="border-t-[#5d6b8b]"
              iconHoverColor="group-hover:text-[#ffa000]"
              illustration={<Users className="w-10 h-10" strokeWidth={1.5} />}
            />
          </>
        );

      case INVENTORY_SUB_TABS.SALES:
        return (
          <>
            <StatCard
              variant="luxury"
              title="Total Revenue"
              iconColor="text-brand-orange"
              value={`${fmt(salesStats.totalRevenue)}`}
              isExpanded={isExpanded}
              topColor="border-t-[#5d6b8b]"
              iconHoverColor="group-hover:text-[#ffa000]"
              illustration={<TrendingUp className="w-10 h-10" strokeWidth={1.5} />}
            >
              <DonutWithLegend data={stock.distribution} label="Vendor Distribution" labelColor="text-emerald-600" />
            </StatCard>
            <StatCard
              variant="luxury"
              title="Items Sold"
              value={salesStats.totalQty}
              isExpanded={isExpanded}
              topColor="border-t-[#5d6b8b]"
              iconHoverColor="group-hover:text-[#ffa000]"
              illustration={<ShoppingCart className="w-10 h-10" strokeWidth={1.5} />}
            />
            <StatCard
              variant="luxury"
              title="Orders"
              value={salesStats.count}
              isExpanded={isExpanded}
              topColor="border-t-[#5d6b8b]"
              iconHoverColor="group-hover:text-[#ffa000]"
              illustration={<ShoppingBag className="w-10 h-10" strokeWidth={1.5} />}
            />
            <StatCard
              variant="luxury"
              title="All Vendors"
              value={salesStats.suppliersCount}
              isExpanded={isExpanded}
              topColor="border-t-[#5d6b8b]"
              iconHoverColor="group-hover:text-[#ffa000]"
              illustration={<Users className="w-10 h-10" strokeWidth={1.5} />}
            />
          </>
        );

      case INVENTORY_SUB_TABS.OPEN_STOCK:
        const { openStock } = stats;
        return (
          <>
            <StatCard
              variant="luxury"
              title="Opening Value"
              iconColor="text-brand-orange"
              value={`${fmt(openStock.totalAmount)}`}
              isExpanded={isExpanded}
              topColor="border-t-[#5d6b8b]"
              iconHoverColor="group-hover:text-[#ffa000]"
              illustration={<IndianRupee className="w-10 h-10" strokeWidth={1.5} />}
            >
              <DonutWithLegend data={stock.distribution} label="Category Value Distribution" labelColor="text-blue-600" />
            </StatCard>
            <StatCard
              variant="luxury"
              title="Total Items Qty"
              value={stock.totalUnits}
              isExpanded={isExpanded}
              topColor="border-t-[#5d6b8b]"
              iconHoverColor="group-hover:text-[#ffa000]"
              illustration={<Package className="w-10 h-10" strokeWidth={1.5} />}
            />
            <StatCard
              variant="luxury"
              title="Stock Entries"
              value={openStock.entriesCount}
              isExpanded={isExpanded}
              topColor="border-t-[#5d6b8b]"
              iconHoverColor="group-hover:text-[#ffa000]"
              illustration={<History className="w-10 h-10" strokeWidth={1.5} />}
            />
            <StatCard
              variant="luxury"
              title="Categories"
              value={openStock.categoriesCount || 0}
              isExpanded={isExpanded}
              topColor="border-t-[#5d6b8b]"
              iconHoverColor="group-hover:text-[#ffa000]"
              illustration={<Users className="w-10 h-10" strokeWidth={1.5} />}
            />
          </>
        );

      case INVENTORY_SUB_TABS.ITEMS:
      default:
        return (
          <>
            <StatCard
              variant="luxury"
              title={activeTab === 'solar' ? 'Solar Items' : 'Tower Items'}
              iconColor="text-brand-orange"
              value={stock.totalItems}
              sub={`${stock.totalUnits} Total Units`}
              isExpanded={isExpanded}
              topColor="border-t-[#5d6b8b]"
              iconHoverColor="group-hover:text-[#ffa000]"
              illustration={<Package className="w-10 h-10" strokeWidth={1.5} />}
            >
              <DonutWithLegend data={stock.distribution} label="Stock Distribution" labelColor="text-blue-600" />
            </StatCard>
            <StatCard
              variant="luxury"
              title="Inventory Value"
              value={`${fmt(stock.totalValue)}`}
              sub={`Avg: ${fmt(stock.totalValue / (stock.totalItems || 1))} / type`}
              isExpanded={isExpanded}
              topColor="border-t-[#5d6b8b]"
              iconHoverColor="group-hover:text-[#ffa000]"
              illustration={<IndianRupee className="w-10 h-10" strokeWidth={1.5} />}
            >
              <DonutWithLegend data={stock.distribution} label="Value Distribution" labelColor="text-emerald-600" />
            </StatCard>
            <StatCard
              variant="luxury"
              title="Low Stock"
              value={stock.lowStockCount}
              sub="Items needing attention"
              isExpanded={isExpanded}
              topColor="border-t-[#5d6b8b]"
              iconHoverColor="group-hover:text-[#ffa000]"
              illustration={<Clock className="w-10 h-10" strokeWidth={1.5} />}
            >
              <div className="flex flex-col items-start justify-start h-full">
                <span className="text-[10px] font-bold text-brand-orange mt-2">Risk Heatmap</span>
                
              </div>
            </StatCard>
            <StatCard
              variant="luxury"
              title="Out of Stock"
              value={stock.noStockCount}
              sub="Critical shortfall"
              isExpanded={isExpanded}
              topColor="border-t-[#5d6b8b]"
              iconHoverColor="group-hover:text-[#ffa000]"
              illustration={<Package className="w-10 h-10" strokeWidth={1.5} />}
            />
          </>
        );
    }
  };

  return (
    <div className="space-y-4 relative group/stats">


      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-fr transition-all duration-300">
        {renderStatsContent()}
      </div>
    </div>
  );
};

